import { type VorgangDetailResponse } from '@/adapter/opm-cockpit-backend/useVorgangApi';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';

interface SchrittShape {
  schritt: string;
  initialPayload?: unknown;
}

interface AbarbeitungShape {
  id: string | null;
  schritte: readonly SchrittShape[];
  laufnummer: number;
}

export function findFehlerPayload(
  detail: VorgangDetailResponse | undefined,
  schritt: VorgangProzessSchritt,
): unknown {
  const vorgang = detail?.vorgang;
  const abarbeitungen = vorgang?.abarbeitungen as
    | readonly AbarbeitungShape[]
    | undefined;
  if (!abarbeitungen || abarbeitungen.length === 0) return null;
  const sortedAbarbeitungen = abarbeitungen.toSorted(
    (a, b) => b.laufnummer - a.laufnummer,
  );
  const letzteAbarbeitung =
    abarbeitungen.find((a) => a.id === vorgang?.letzteAbarbeitungId) ??
    sortedAbarbeitungen[0];
  const fehlerSchritt = letzteAbarbeitung.schritte.find(
    (s) => s.schritt === schritt,
  );
  return fehlerSchritt?.initialPayload ?? null;
}

export function getValueAtPath(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const part of parts) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function setValueAtPath(
  obj: unknown,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const parts = path.split('.');
  const root: Record<string, unknown> = isPlainObject(obj) ? { ...obj } : {};
  let cur: Record<string, unknown> = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const child = cur[key];
    const cloned: Record<string, unknown> = isPlainObject(child)
      ? { ...child }
      : {};
    cur[key] = cloned;
    cur = cloned;
  }
  cur[parts[parts.length - 1]] = value;
  return root;
}

export function deleteValueAtPath(
  obj: unknown,
  path: string,
): Record<string, unknown> {
  const parts = path.split('.');
  const root: Record<string, unknown> = isPlainObject(obj) ? { ...obj } : {};
  let cur: Record<string, unknown> = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const child = cur[key];
    if (!isPlainObject(child)) return root;
    const cloned: Record<string, unknown> = { ...child };
    cur[key] = cloned;
    cur = cloned;
  }
  delete cur[parts[parts.length - 1]];
  return root;
}

// Prüft, ob `path` ein echter Nachfahre von `ancestor` ist — nur ganze
// Pfad-Segmente matchen, damit z. B. "foobar" nicht als Nachfahre von "foo" gilt.
export function isDescendantPath(path: string, ancestor: string): boolean {
  if (ancestor === '') return path !== '';
  return path.startsWith(`${ancestor}.`);
}

export type PathType = 'string' | 'number' | 'boolean' | 'object';

export function inferPathType(value: unknown): PathType {
  if (typeof value === 'string') return 'string';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'object';
}

export function parseUserValue(input: string, type: PathType): unknown {
  switch (type) {
    case 'string':
      return input;
    case 'number': {
      const n = Number(input);
      return Number.isFinite(n) ? n : input;
    }
    case 'boolean':
      if (input === 'true') return true;
      if (input === 'false') return false;
      return input;
    case 'object':
      return {};
  }
}
